"use client";

import { ArrowRight, Clock, Loader, Mail, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const contactItems = [{
  icon: Clock,
  label: "GARDEN HOURS",
  value: "SUN—FRI, 08—20"
}, {
  icon: Mail,
  label: "EMAIL",
  value: "GROW@ECOGROW.ORG"
}, {
  icon: Phone,
  label: "PHONE",
  value: "+1 555-012-8899"
}];
const plotSizes = [{
  value: "small",
  label: "Starter Plot (4x4)"
}, {
  value: "medium",
  label: "Family Plot (8x8)"
}, {
  value: "large",
  label: "Community Plot (12x12)"
}, {
  value: "raised",
  label: "Raised Bed"
}];
const membershipTypes = [{
  value: "annual",
  label: "Annual"
}, {
  value: "seasonal",
  label: "Seasonal"
}, {
  value: "volunteer",
  label: "Volunteer"
}, {
  value: "workshop",
  label: "Workshop"
}];
function InquiryForm() {
  const {
    control,
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 border border-border bg-card rounded-lg">
        <p className="font-mono text-xs uppercase tracking-widest text-chart-2 mb-3">
          N°.04 — RECEIVED
        </p>
        <p className="font-serif text-2xl text-foreground mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="font-mono text-xs uppercase tracking-widest rounded-lg border-border bg-transparent text-foreground hover:bg-accent">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <FormField control={control} name="name" rules={{
      required: "Name is required",
      minLength: {
        value: 2,
        message: "Min 2 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
              Full Name
            </FormLabel>
            <FormControl>
              <Input placeholder="Your full name" className="border-0 border-b border-border rounded-md bg-transparent px-0 py-3 h-auto text-foreground placeholder:text-muted-foreground/60 focus-visible:border-chart-2 focus-visible:ring-0 shadow-sm" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Invalid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
              Email Address
            </FormLabel>
            <FormControl>
              <Input type="email" placeholder="you@example.com" className="border-0 border-b border-border rounded-md bg-transparent px-0 py-3 h-auto text-foreground placeholder:text-muted-foreground/60 focus-visible:border-chart-2 focus-visible:ring-0 shadow-sm" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="plotSize" rules={{
      required: "Please select a plot size"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
              Preferred Plot Size
            </FormLabel>
            <FormControl>
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger className="border-0 border-b border-border rounded-md bg-transparent px-0 py-3 h-auto w-full text-foreground data-[placeholder]:text-muted-foreground/60 focus-visible:border-chart-2 focus-visible:ring-0 shadow-sm">
                  <SelectValue placeholder="Select a size" />
                </SelectTrigger>
                <SelectContent className="bg-popover border-border rounded-lg">
                  {plotSizes.map((type, i) => <SelectItem key={type.value} data-index={i} value={type.value} className="font-default text-popover-foreground focus:bg-accent focus:text-accent-foreground rounded-md">
                      {type.label}
                    </SelectItem>)}
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="membershipType" rules={{
      required: "Please select a membership type"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
              Membership Type
            </FormLabel>
            <FormControl>
              <RadioGroup onValueChange={field.onChange} value={field.value} className="grid grid-cols-2 gap-3 pt-2">
                {membershipTypes.map((option, i) => <label key={option.value} data-index={i} htmlFor={`membership-${option.value}`} className="flex items-center gap-3 border border-border p-4 cursor-pointer hover:border-chart-2 transition-colors rounded-lg">
                    <RadioGroupItem value={option.value} id={`membership-${option.value}`} className="border-border text-chart-2" />
                    <span className="font-mono text-xs uppercase tracking-widest text-foreground">
                      {option.label}
                    </span>
                  </label>)}
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="details" rules={{
      required: "Please tell us about your gardening goals",
      minLength: {
        value: 10,
        message: "Min 10 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
              Tell us about your gardening goals
            </FormLabel>
            <FormControl>
              <Textarea rows={5} placeholder="What do you hope to grow this season?" className="border border-border rounded-lg bg-transparent px-4 py-3 text-foreground placeholder:text-muted-foreground/60 focus-visible:border-chart-2 focus-visible:ring-0 resize-none shadow-sm" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="newsletter" render={({
      field
    }) => <FormItem>
            <div className="flex items-center gap-3">
              <FormControl>
                <Checkbox checked={!!field.value} onCheckedChange={field.onChange} className="border-border data-[state=checked]:bg-chart-2 data-[state=checked]:border-chart-2 rounded-md" />
              </FormControl>
              <FormLabel className="font-mono text-xs uppercase tracking-widest text-foreground cursor-pointer">
                Subscribe to our seasonal newsletter
              </FormLabel>
            </div>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 border border-destructive bg-destructive/10 rounded-lg">
          <p className="font-mono text-xs text-destructive">{submitError}</p>
        </div>}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-4 border-t border-border">
        <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
          We review applications weekly.
        </p>
        <Button type="submit" disabled={isSubmitting} className="font-mono text-xs uppercase tracking-widest bg-chart-2 text-primary-foreground hover:bg-chart-2/90 rounded-lg px-8 py-6 h-auto self-start sm:self-auto">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Sending
            </> : <>
              Submit Application
              <ArrowRight className="ml-2 size-4" />
            </>}
        </Button>
      </div>
    </Form>;
}
export default function PlotRentalInquiry() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="5129" id="plot-rental-inquiry" className="dark bg-background text-foreground py-24 md:py-32 px-6 md:px-12 lg:px-20">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div ref={leftRef} className={`lg:col-span-5 transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <p className="font-mono text-xs uppercase tracking-widest text-chart-2 mb-8">PLOT RENTAL</p>
            <h2 className="font-serif font-light text-4xl md:text-5xl lg:text-[3.5rem] leading-[1.05] text-foreground mb-8">
              Start your
              <br />
              <span className="italic text-muted-foreground">growing journey.</span>
            </h2>
            <p className="font-default text-base md:text-lg text-muted-foreground leading-relaxed mb-12 max-w-md">
              Join our vibrant community of urban farmers. Whether you are a seasoned gardener or just starting out, we have a plot waiting for you to cultivate.
            </p>

            <div className="border-t border-border pt-8 space-y-5">
              <p className="font-mono text-xs uppercase tracking-widest text-chart-2 mb-2">
                The Garden
              </p>
              {contactItems.map((item, i) => {
              const Icon = item.icon;
              return <div key={item.label} data-index={i} className="flex items-start gap-4">
                    <Icon className="size-4 text-muted-foreground mt-0.5 shrink-0" />
                    <div className="flex flex-col">
                      <span className="font-mono text-[0.65rem] uppercase tracking-widest text-muted-foreground/70">
                        {item.label}
                      </span>
                      <span className="font-mono text-xs uppercase tracking-widest text-foreground">
                        {item.value}
                      </span>
                    </div>
                  </div>;
            })}
            </div>
          </div>

          <div ref={rightRef} className={`lg:col-span-7 transition-all duration-1000 delay-200 ease-out ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <FormProvider formKey="plot_rental_inquiry_join_us" formId="plot_rental_inquiry_join_us" sectionName="plot-rental-inquiry" className="border border-border bg-card p-8 md:p-12 rounded-lg" defaultValues={{
            name: "",
            email: "",
            plotSize: "",
            membershipType: "",
            details: "",
            newsletter: false
          }} validationRules={{
            name: {
              required: true,
              minLength: 2
            },
            email: {
              required: true,
              pattern: "email"
            },
            plotSize: {
              required: true
            },
            membershipType: {
              required: true
            },
            details: {
              required: true,
              minLength: 10
            }
          }} submitText="Submit Application" successMessage="Application received! Our team will reach out to confirm your plot availability." errorMessage="We couldn't process your application. Please try again.">
              <p className="font-mono text-xs uppercase tracking-widest text-chart-2 mb-2">
                N°.03 / Application
              </p>
              <h3 className="font-serif text-2xl md:text-3xl text-foreground mb-8">
                Request a Plot
              </h3>
              <InquiryForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}